import LandingPage from "pages/LandingPage";
import { Route, Switch, useHistory, useLocation } from "react-router-dom";
import { RouteList, RouteTable } from "Routes";
import { Redirects, ParameterizedRedirects } from "Redirects";
import { Fragment, Suspense, useContext, useEffect, useState } from "react";
import { GlobalContext } from "contexts/GlobalContext";
import { ToastContainer } from "components/ToastContainer";
import { WindowConfirm } from "components/WindowConfirm";
import { Navbar } from "components/Navbar";
import { PageHeader } from "components/PageHeader";
import { SignUpLogInModal } from "components/modals/SignUpLogInModal";
import { SubscribeModal } from "components/modals/SubscribeModal";
import { ErrorPage } from "pages/routes/ErrorPage";
import { AuthBlockedPage } from "pages/routes/AuthBlockedPage";
import { AuthRequiredPage } from "pages/routes/AuthRequiredPage";
import { NotStudentsPage } from "pages/routes/NotStudentsPage";
import { TeachersPage } from "pages/routes/TeachersPage";

export const Routes = () => {
    const history = useHistory();
    const location = useLocation();

    const { error, setError, windowConfirm, clearNotifications, isAuthenticated, globalData, userData } =
        useContext(GlobalContext);

    // redirects
    const [hasCheckedRedirects, setHasCheckedRedirects] = useState(false);

    useEffect(() => {
        const pathName = location.pathname.toLowerCase();

        // check simple redirects first
        const hasSimpleRedirect = Redirects.has(pathName);

        if (hasSimpleRedirect) {
            const redirect = Redirects.get(pathName);

            if (redirect) {
                history.push(redirect);
                return;
            }
        }

        // check parameterized redirects
        for (const paramRedirect of ParameterizedRedirects) {
            const match = pathName.match(paramRedirect.sourcePattern);

            if (match) {
                const params = match.slice(1); // remove the full match, keep only capture groups
                const redirectUrl = paramRedirect.targetFunction(params);
                history.push(redirectUrl);
                return;
            }
        }

        setHasCheckedRedirects(true);
    }, [history, location]);

    useEffect(() => {
        history.listen(() => {
            // clear errors on page change
            setError(undefined);

            // reset notifications
            clearNotifications();

            // clear redirect
            setHasCheckedRedirects(false);
        });
    }, [clearNotifications, history, setError]);

    return (
        <Fragment>
            {error ? (
                <ErrorPage />
            ) : (
                <Suspense
                    fallback={
                        <div className="vstack gap-4 gap-lg-5">
                            <Navbar />

                            <div className="container">
                                <PageHeader
                                    icon={<span className="fas fa-spin fa-loader"></span>}
                                    title=""
                                    description=""
                                />
                            </div>
                        </div>
                    }
                >
                    <Switch>
                        {RouteTable.map(({ path, Component, authType }) => (
                            <Route exact path={path} key={path}>
                                {({ match }) => {
                                    if (isAuthenticated === undefined) {
                                        return null;
                                    }

                                    if (authType === "AuthBlocked" && isAuthenticated) {
                                        return <AuthBlockedPage />;
                                    }

                                    if (authType === "Teachers" && !isAuthenticated) {
                                        return <AuthRequiredPage redirect={RouteList.LoginTeacher} />;
                                    }

                                    if (authType === "AuthRequired" && !isAuthenticated) {
                                        return <AuthRequiredPage redirect={RouteList.Login} />;
                                    }

                                    if (authType === "NotStudents" && userData?.isStudent) {
                                        return <NotStudentsPage />;
                                    }

                                    if (authType === "Teachers" && userData?.isStudent) {
                                        return <TeachersPage />;
                                    }

                                    // key on the router's search (not window.location.search) so
                                    // silent replaceState URL rewrites don't remount the page
                                    return <Component {...match?.params} key={location.search} />;
                                }}
                            </Route>
                        ))}

                        {hasCheckedRedirects && (
                            <Route path="*">
                                <LandingPage />
                            </Route>
                        )}
                    </Switch>
                </Suspense>
            )}

            <WindowConfirm {...windowConfirm} />

            {!isAuthenticated && <SignUpLogInModal />}
            {isAuthenticated && !globalData?.isSubscribed && !userData?.isStudent && <SubscribeModal />}

            <ToastContainer />
        </Fragment>
    );
};
